"use client";

import { Switch as SwitchPrimitive } from "@base-ui/react/switch";
import * as React from "react";
import { NumericFormat } from "react-number-format";

import {
  GRID_ELBOW_FRAME_BOTTOM_LEFT,
  GRID_ELBOW_FRAME_BOTTOM_RIGHT,
  GRID_ELBOW_FRAME_TOP_LEFT,
  GRID_ELBOW_FRAME_TOP_RIGHT,
  GridElbow,
} from "@/components/ui/grid-connector";
import { Input } from "@/components/ui/input";
import { SliderRoot, SliderThumb } from "@/components/ui/slider";
import { AMOUNT_SLIDER_STEPS, clamp } from "@/lib/pricing-calculator/defaults";
import { cn } from "@/lib/utils";

/* -------------------------------------------------------------------------- */
/* Field                                                                       */
/* -------------------------------------------------------------------------- */

interface FieldProps {
  id: string;
  label: string;
  hint?: string;
  /** Rendered on the right of the label row, e.g. the paired value input. */
  trailing?: React.ReactNode;
  children: React.ReactNode;
  className?: string;
}

/** Id of a field's hint, for the controls' `aria-describedby`. */
export function fieldHintId(id: string): string {
  return `${id}-hint`;
}

/** Label row with optional trailing control, hint underneath the control. */
export function Field({
  id,
  label,
  hint,
  trailing,
  children,
  className,
}: FieldProps) {
  return (
    <div className={cn("flex flex-col gap-2", className)}>
      <div className="flex items-center justify-between gap-4">
        <label htmlFor={id} className="text-ds-body-sm text-grey-700">
          {label}
        </label>
        {trailing}
      </div>
      {children}
      {hint && (
        <p id={fieldHintId(id)} className="text-ds-body-xs text-grey-500">
          {hint}
        </p>
      )}
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* Segmented control                                                           */
/* -------------------------------------------------------------------------- */

export interface SegmentedOption<T extends string> {
  value: T;
  label: string;
  hint?: string;
}

interface SegmentedControlProps<T extends string> {
  value: T;
  onChange: (value: T) => void;
  options: readonly SegmentedOption<T>[];
  "aria-label": string;
  size?: "sm" | "lg";
  className?: string;
}

/**
 * Pill radiogroup. `lg` shows a title and hint per option (structure picker);
 * `sm` is a compact label-only toggle (frequency).
 */
export function SegmentedControl<T extends string>({
  value,
  onChange,
  options,
  size = "sm",
  className,
  ...rest
}: SegmentedControlProps<T>) {
  const refs = React.useRef<Map<T, HTMLButtonElement>>(new Map());

  const focusOption = (option: T) => {
    refs.current.get(option)?.focus();
  };

  const onKeyDown = (
    event: React.KeyboardEvent<HTMLButtonElement>,
    index: number
  ) => {
    const delta =
      event.key === "ArrowRight" || event.key === "ArrowDown"
        ? 1
        : event.key === "ArrowLeft" || event.key === "ArrowUp"
          ? -1
          : 0;
    if (delta === 0) return;
    event.preventDefault();
    const next = options[(index + delta + options.length) % options.length];
    if (!next) return;
    onChange(next.value);
    focusOption(next.value);
  };

  return (
    <div
      role="radiogroup"
      aria-label={rest["aria-label"]}
      className={cn(
        "bg-grey-100 rounded-ds-full grid gap-1 p-1",
        size === "lg"
          ? "rounded-ds-xl grid-cols-2"
          : "inline-grid auto-cols-fr grid-flow-col",
        className
      )}
    >
      {options.map((option, index) => {
        const active = option.value === value;
        return (
          <button
            key={option.value}
            ref={(node) => {
              if (node) refs.current.set(option.value, node);
              else refs.current.delete(option.value);
            }}
            type="button"
            role="radio"
            aria-checked={active}
            tabIndex={active ? 0 : -1}
            onClick={() => onChange(option.value)}
            onKeyDown={(event) => onKeyDown(event, index)}
            className={cn(
              "flex min-w-0 flex-col items-center justify-center text-center transition-[background-color,color,box-shadow] duration-[180ms] [transition-timing-function:cubic-bezier(0.2,0.8,0.2,1)]",
              "focus-visible:shadow-[0_0_0_2px_var(--grey-0),0_0_0_4px_var(--pulse-700)] focus-visible:outline-none",
              size === "lg"
                ? "rounded-ds-l min-h-[60px] px-3 py-2.5"
                : "rounded-ds-full h-9 px-4",
              active
                ? "bg-grey-0 text-grey-950 shadow-ds-xs"
                : "text-grey-600 hover:text-grey-950"
            )}
          >
            <span
              className={cn(
                size === "lg"
                  ? "text-ds-body-md font-medium"
                  : "text-ds-body-sm font-medium"
              )}
            >
              {option.label}
            </span>
            {size === "lg" && option.hint && (
              <span
                className={cn(
                  "text-ds-body-xs mt-0.5",
                  active ? "text-pulse-700" : "text-grey-500"
                )}
              >
                {option.hint}
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* Numeric inputs                                                              */
/* -------------------------------------------------------------------------- */

interface NumberInputProps {
  id: string;
  value: number;
  onChange: (value: number) => void;
  min: number;
  max: number;
  /** Decimal places accepted while typing. */
  decimalScale?: number;
  prefix?: string;
  suffix?: string;
  allowNegative?: boolean;
  step?: number;
  className?: string;
  inputClassName?: string;
  placeholder?: string;
  "aria-label"?: string;
  "aria-describedby"?: string;
}

/**
 * Formatted number field. Holds a free draft while typing and commits a
 * clamped value on blur or Enter, so a half-typed figure never snaps back.
 */
export function NumberInput({
  id,
  value,
  onChange,
  min,
  max,
  decimalScale = 0,
  prefix,
  suffix,
  allowNegative = false,
  className,
  inputClassName,
  placeholder,
  ...aria
}: NumberInputProps) {
  const [draft, setDraft] = React.useState<number | undefined>(value);
  const [focused, setFocused] = React.useState(false);

  React.useEffect(() => {
    if (!focused) setDraft(value);
  }, [value, focused]);

  const commit = () => {
    const next = clamp(draft ?? min, min, max);
    setDraft(next);
    if (next !== value) onChange(next);
  };

  return (
    <div className={cn("relative", className)}>
      {prefix && (
        <span
          aria-hidden
          className="text-grey-500 text-ds-body-md pointer-events-none absolute top-1/2 left-3 -translate-y-1/2"
        >
          {prefix}
        </span>
      )}
      <NumericFormat
        id={id}
        value={draft ?? ""}
        placeholder={placeholder}
        customInput={Input}
        thousandSeparator=","
        decimalScale={decimalScale}
        allowNegative={allowNegative}
        inputMode="decimal"
        autoComplete="off"
        onValueChange={(values) => setDraft(values.floatValue)}
        onFocus={() => setFocused(true)}
        onBlur={() => {
          setFocused(false);
          commit();
        }}
        onKeyDown={(event) => {
          if (event.key === "Enter") {
            event.preventDefault();
            (event.target as HTMLInputElement).blur();
          }
        }}
        className={cn(
          "tabular-nums",
          prefix && "pl-7",
          suffix && "pr-11",
          inputClassName
        )}
        {...aria}
      />
      {suffix && (
        <span
          aria-hidden
          className="text-grey-500 text-ds-body-md pointer-events-none absolute top-1/2 right-3 -translate-y-1/2"
        >
          {suffix}
        </span>
      )}
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* Slider + value input                                                        */
/* -------------------------------------------------------------------------- */

interface SliderFieldProps {
  id: string;
  label: string;
  hint?: string;
  value: number;
  onChange: (value: number) => void;
  /** Slider travel. */
  min: number;
  max: number;
  step?: number;
  /** Typed bounds; default to the slider bounds. */
  typedMin?: number;
  typedMax?: number;
  decimalScale?: number;
  prefix?: string;
  suffix?: string;
  /** Formats the accessible value text for the slider thumb. */
  formatValue?: (value: number) => string;
  /** Optional mapping for non-linear sliders. */
  toSlider?: (value: number) => number;
  fromSlider?: (position: number) => number;
  /** Tick labels rendered under the track at evenly spaced positions. */
  ticks?: readonly string[];
  inputWidthClassName?: string;
}

export function SliderField({
  id,
  label,
  hint,
  value,
  onChange,
  min,
  max,
  step = 1,
  typedMin,
  typedMax,
  decimalScale = 0,
  prefix,
  suffix,
  formatValue,
  toSlider,
  fromSlider,
  ticks,
  inputWidthClassName = "w-28",
}: SliderFieldProps) {
  const nonLinear = Boolean(toSlider && fromSlider);
  const sliderValue = toSlider ? toSlider(value) : clamp(value, min, max);
  const sliderMin = nonLinear ? 0 : min;
  const sliderMax = nonLinear ? AMOUNT_SLIDER_STEPS : max;
  const sliderStep = nonLinear ? 1 : step;
  const handleSlider = (values: number[]) => {
    const [position] = values;
    if (position === undefined) return;
    onChange(fromSlider ? fromSlider(position) : position);
  };

  return (
    <Field
      id={id}
      label={label}
      hint={hint}
      trailing={
        <NumberInput
          id={id}
          value={value}
          onChange={onChange}
          min={typedMin ?? min}
          max={typedMax ?? max}
          decimalScale={decimalScale}
          prefix={prefix}
          suffix={suffix}
          className={inputWidthClassName}
          inputClassName="h-10 text-right"
          aria-describedby={hint ? fieldHintId(id) : undefined}
        />
      }
    >
      {/* Track runs edge to edge so it aligns with the label and hint. */}
      <div className={cn(ticks && "pb-4")}>
        <SliderRoot
          value={[sliderValue]}
          min={sliderMin}
          max={sliderMax}
          step={sliderStep}
          onValueChange={handleSlider}
          rangeClassName="bg-pulse-700"
          aria-label={label}
          className="py-2"
        >
          <SliderThumb
            aria-label={label}
            aria-describedby={hint ? fieldHintId(id) : undefined}
            aria-valuetext={formatValue ? formatValue(value) : undefined}
            className="border-grey-0 size-5 border-[3px] shadow-[0_0_0_1px_var(--pulse-700)] focus-visible:shadow-[0_0_0_1px_var(--pulse-700),0_0_0_5px_var(--pulse-100)] focus-visible:ring-0 focus-visible:ring-offset-0"
          />
        </SliderRoot>
        {ticks && ticks.length > 1 && (
          <div className="relative mt-1 h-4" aria-hidden>
            {ticks.map((tick, index) => (
              <span
                key={`${tick}-${index}`}
                className="text-grey-500 text-ds-label-md absolute -translate-x-1/2 whitespace-nowrap tabular-nums"
                style={{ left: `${(index / (ticks.length - 1)) * 100}%` }}
              >
                {tick}
              </span>
            ))}
          </div>
        )}
      </div>
    </Field>
  );
}

/* -------------------------------------------------------------------------- */
/* Switch                                                                      */
/* -------------------------------------------------------------------------- */

interface SwitchProps {
  id?: string;
  checked: boolean;
  onCheckedChange: (checked: boolean) => void;
  "aria-label"?: string;
  "aria-labelledby"?: string;
}

export function Switch({ checked, onCheckedChange, ...props }: SwitchProps) {
  return (
    <SwitchPrimitive.Root
      checked={checked}
      onCheckedChange={onCheckedChange}
      className={cn(
        "rounded-ds-full relative inline-flex h-6 w-11 shrink-0 cursor-pointer items-center transition-colors duration-[180ms] [transition-timing-function:cubic-bezier(0.2,0.8,0.2,1)]",
        "data-checked:bg-pulse-700 data-unchecked:bg-grey-300",
        "focus-visible:shadow-[0_0_0_2px_var(--grey-0),0_0_0_4px_var(--pulse-700)] focus-visible:outline-none"
      )}
      {...props}
    >
      <SwitchPrimitive.Thumb className="bg-grey-0 rounded-ds-full shadow-ds-xs block size-5 translate-x-0.5 transition-transform duration-[180ms] [transition-timing-function:cubic-bezier(0.2,0.8,0.2,1)] data-checked:translate-x-[22px]" />
    </SwitchPrimitive.Root>
  );
}

/* -------------------------------------------------------------------------- */
/* Card                                                                        */
/* -------------------------------------------------------------------------- */

/** Which panel corners carry the redesign's grid-connector L marks. */
export type FrameConnectors = "top" | "bottom" | "both" | "none";

/**
 * Corner L marks where a white panel meets the grey page, matching the
 * homepage's bordered frames (`components/blocks/bordered-frame.tsx`).
 * Desktop only, like the shared frame.
 */
export function FrameCorners({ connectors }: { connectors: FrameConnectors }) {
  if (connectors === "none") return null;
  const top = connectors === "top" || connectors === "both";
  const bottom = connectors === "bottom" || connectors === "both";
  return (
    <>
      {top && (
        <>
          <GridElbow
            corner="top-left"
            className={cn(GRID_ELBOW_FRAME_TOP_LEFT, "z-10 hidden lg:block")}
          />
          <GridElbow
            corner="top-right"
            className={cn(GRID_ELBOW_FRAME_TOP_RIGHT, "z-10 hidden lg:block")}
          />
        </>
      )}
      {bottom && (
        <>
          <GridElbow
            corner="bottom-left"
            className={cn(GRID_ELBOW_FRAME_BOTTOM_LEFT, "z-10 hidden lg:block")}
          />
          <GridElbow
            corner="bottom-right"
            className={cn(
              GRID_ELBOW_FRAME_BOTTOM_RIGHT,
              "z-10 hidden lg:block"
            )}
          />
        </>
      )}
    </>
  );
}

interface CardProps extends React.HTMLAttributes<HTMLDivElement> {
  connectors?: FrameConnectors;
  /** Collapse the top border into the panel above (stacked panels). */
  stack?: boolean;
}

/** Square white panel with a hairline border, the redesign's frame idiom. */
export function Card({
  children,
  className,
  connectors = "none",
  stack = false,
  ...props
}: CardProps) {
  return (
    <div
      className={cn(
        "border-grey-100 bg-grey-0 relative border p-5 md:p-7",
        stack && "-mt-px border-t-0",
        className
      )}
      {...props}
    >
      <FrameCorners connectors={connectors} />
      {children}
    </div>
  );
}
